import { reverse } from 'named-urls';
import React from 'react';
import { useDashboardPermissions } from '@components';
import { ButtonClean, ProfitString, Summary, useModalContext } from '@fourthwall/components';
import { pluralize } from '@fourthwall/utils/dist/string';
import { getNumberOfProducts } from '@utils/products';
import { routing } from '@utils/routing';
import { SummaryRowLabel } from '../../../../components/SummaryRowLabel/SummaryRowLabel';
import { OrderCostsBreakdownModal } from '../../modules/OrderCostsBreakdownModal';
import { ErrorBadge } from '../ErrorBadge/ErrorBadge';
import * as S from './OrderProfitSummary.styled';
export const OrderProfitSummary = ({ order, shippingMethodTooltip, totalRefundedAmount, }) => {
    const { open } = useModalContext();
    const { hasPermission } = useDashboardPermissions();
    const numberOfProducts = getNumberOfProducts(order.offers) + getNumberOfProducts(order.offersCancelled);
    const getShippingMethodLabel = () => {
        if (order.shipments.length > 1) {
            return 'Shipping (Multiple shipments)';
        }
        return order.shipping.method || '';
    };
    const getPromotionLabel = () => {
        if (!order || !order.promotion)
            return null;
        return order.promotion.isAutoApplied ? order.promotion.title : order.promotion.code;
    };
    const getDiscountLabel = () => {
        if (!order || !order.promotion)
            return;
        const label = order.type === 'GIFT' ? 'Gift discount' : 'Discount';
        const promotionLabel = getPromotionLabel();
        return (React.createElement(React.Fragment, null,
            label,
            ' ',
            promotionLabel && (React.createElement(React.Fragment, null,
                "(",
                React.createElement(ButtonClean, { label: promotionLabel, to: reverse(routing.promotions.promotionCodes.promotionCode.self, {
                        promotionId: order.promotion.id,
                    }) }),
                ")"))));
    };
    const getRefundLabel = () => {
        if (order.refund.anyRefundIssuedManually) {
            if (order.refund.anyRefundIssuedViaCancel) {
                // If includes both manual and cancel refunds
                return 'Refunds & adjustments';
            }
            // If includes manual refund only
            return 'Manual refunds';
        }
        // If includes cancel refund only
        return 'Refunded (canceled items)';
    };
    const renderRefundSummaryRow = () => {
        if (!totalRefundedAmount?.value || totalRefundedAmount.value <= 0) {
            return null;
        }
        return (React.createElement(Summary.Row, { label: getRefundLabel(), value: {
                value: totalRefundedAmount.value * -1,
                currency: totalRefundedAmount.currency,
            } }));
    };
    const renderProfitSummaryRow = () => {
        if (!order)
            return null;
        if (!hasPermission('contributions.profit'))
            return null;
        const value = !order.profit ? (React.createElement(ErrorBadge, null)) : (React.createElement(React.Fragment, null,
            React.createElement("span", { style: { marginRight: 8, fontSize: 12, fontWeight: 400 } },
                React.createElement(ButtonClean, { label: "Show breakdown", appearance: "secondary", onClick: () => {
                        open(OrderCostsBreakdownModal, { orderId: order.id, order });
                    } })),
            React.createElement(ProfitString, { value: order.profit.value })));
        return React.createElement(Summary.Row, { label: "Your profit", value: value, bold: true });
    };
    return (React.createElement(S.Container, null,
        React.createElement(Summary, null,
            React.createElement(Summary.Row, { label: `Subtotal (${pluralize(numberOfProducts, `${numberOfProducts} item`, `${numberOfProducts} items`)})`, value: order.merchandiseTotal }),
            order.shipping.method && (React.createElement(Summary.Row, { label: React.createElement(SummaryRowLabel, { title: getShippingMethodLabel(), tooltip: shippingMethodTooltip }), value: order.shipping.price })),
            order.totalTax?.total && React.createElement(Summary.Row, { label: "Tax", value: order.totalTax.total }),
            order.paidPaymentFee && (React.createElement(Summary.Row, { label: "Payment processing fees", value: order.paidPaymentFee })),
            !!order?.discount && (React.createElement(Summary.Row, { label: getDiscountLabel(), value: {
                    value: order.currentAmounts.discount.value > 0
                        ? order.currentAmounts.discount.value * -1
                        : order.currentAmounts.discount.value,
                    currency: order.currentAmounts.discount.currency,
                } })),
            !!order.creatorBudget?.usedBudget && (React.createElement(Summary.Row, { label: "Samples budget", value: {
                    value: order.creatorBudget.usedBudget.value * -1,
                    currency: order.creatorBudget.usedBudget.currency,
                } })),
            renderRefundSummaryRow(),
            React.createElement(S.Separator, null),
            React.createElement(Summary.Row, { label: "Paid by supporter", value: order?.paidBySupporterFinal, bold: true }),
            renderProfitSummaryRow())));
};
